import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppStore } from "../../../redux/store";
import { getAuthToken, unsetAuth } from "../../../helpers/authHelper";
import useCallApiAndLoad from "../../../hooks/useCallApiAndLoad";
import { getTenantProfileEndpoint } from "../../Tenant/services/tenantService";
import { logoutAction } from "../../../redux/features/auth";
import { fetchResponseAdapter } from "../../../adapters/fetchAdapter";
import { reverseDate } from "../../../helpers/formHelpers";
import TenantProfileLoading from "../../Tenant/components/Loading/TenantProfileLoading";
import WhiteModal from "../../../components/WhiteModel";
import UploadAvatar from "../../../components/UploadAvatar";
import { DEFAULT_AVATAR } from "../../../constants/ProfileConstants";
import Button from "../../../components/form/Button";
import {
  IconAlertCircle,
  IconAlertCircleFilled,
  IconCameraRotate,
  IconRosetteDiscountCheckFilled,
} from "@tabler/icons-react";
import TabsComponent from "../../Developers/components/TabsComponent/TabsComponent";
import { useCustomerProfile } from "./hooks/useProfile";
import AvatarModal from "../../../components/WhiteModel";

const EditProfile = () => {
  const { country } = useSelector((store: AppStore) => store.country);
  const user = useSelector((store: AppStore) => store.auth);
  const token = getAuthToken(user);
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [apiData, setApiData] = useState<any>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showAvatarModal, setShowAvatarModal] = useState(false);

  const { getCustomerInfoData, customerData, loadingCustomer } =
    useCustomerProfile();

  useEffect(() => {
    getCustomerInfoData();
  }, []);

  useEffect(() => {
    const loadTenantProfile = async () => {
      const { status, data } = await callEndpoint(
        getTenantProfileEndpoint(token)
      );
      if (status === 401) {
        dispatch(logoutAction({}));
        unsetAuth();
        return navigate(`/app/${country}/auth/login`);
      }
      const response = fetchResponseAdapter(data);

      setApiData({
        birth_date_default: response.data.birth_date
          ? reverseDate(response.data.birth_date)
          : "",
        ...response.data,
      });
    };
    loadTenantProfile().catch((e) => console.error(e));
  }, []);

  const toggleAvatarModal = () => {
    try {
      setShowAvatarModal((old) => !old);
      document.body.style.overflowY = "scroll";
    } catch (error) {
      console.log("something went wrong with bidder info");
    }
  };

  const handleEdit = () => {
    navigate(`/app/${country}/l/tn/update-profile`, {
      state: apiData,
    });
  };

  if (isLoading) {
    return <TenantProfileLoading />;
  }

  const isVerified =
    !customerData?.required_actions?.require_documents &&
    !customerData?.required_actions?.require_phone_verification;

  return (
    <>
      {showAvatarModal && (
        <AvatarModal
          showAvatarModal={showAvatarModal}
          toggleAvatarModal={toggleAvatarModal}
        />
      )}

      <div className="w-full pb-20 pt-10">
        <div className="container mx-auto">
          <div className="w-full px-4 md:px-0">
            <div className="flex w-full justify-start items-center gap-6">
              <div className=" justify-center w-auto">
                <div className="w-28 h-28 lg:w-40 lg:h-40 rounded-xl relative">
                  <img
                    src={
                      apiData && apiData.avatar
                        ? apiData.avatar
                        : DEFAULT_AVATAR
                    }
                    key={
                      apiData && apiData.avatar
                        ? apiData.avatar
                        : DEFAULT_AVATAR
                    }
                    className=" w-full h-full object-cover object-center"
                    alt="avatar"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // this prevents a checking looping
                      currentTarget.src = DEFAULT_AVATAR;
                    }}
                  />
                </div>
                <Button
                  className="block w-full mt-4 text-xs lg:text-sm"
                  variant="outlined"
                  onClick={toggleAvatarModal}
                >
                  Actualizar foto
                </Button>
              </div>
              <div className="">
                <h2 className="text-2xl font-semibold text-[#212121] flex justify-start items-center gap-2">
                  {customerData?.first_name + " " + customerData?.last_name}
                  <span>
                    {isVerified ? (
                      <IconRosetteDiscountCheckFilled color="#2fb672" />
                    ) : (
                      <IconAlertCircle color="#ff5252" />
                    )}
                  </span>
                </h2>

                <p className="text-base font-light text-[#757575]">
                  {isVerified ? "Usuario verificado" : "Usuario por verificar"}
                </p>

                <div className=" hidden max-w-lg lg:flex flex-col gap-2 mt-4">
                  {customerData?.required_actions?.require_documents && (
                    <span className="py-0.5 px-4 flex justify-start items-center gap-2 rounded-lg text-xs font-medium bg-[#FFF3D6] text-[#A15C07]">
                      <IconAlertCircleFilled width={14} height={14} /> Es
                      necesario proporcionar un documento de identificación
                      válido
                    </span>
                  )}
                  {customerData?.required_actions
                    ?.require_phone_verification && (
                    <span className="py-0.5 px-4 flex justify-start items-center gap-2 rounded-lg text-xs font-medium bg-[#FFF3D6] text-[#A15C07]">
                      <IconAlertCircleFilled width={14} height={14} /> Es
                      necesario proporcionar un número de contacto válido
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className=" flex max-w-lg lg:hidden flex-col gap-2 mt-4">
              {customerData?.required_actions?.require_documents && (
                <span className="py-0.5 px-4 flex justify-start items-center gap-2 rounded-lg text-xs font-medium bg-[#FFF3D6] text-[#A15C07]">
                  <IconAlertCircleFilled width={14} height={14} /> Es necesario
                  proporcionar un documento de identificación válido
                </span>
              )}
              {customerData?.required_actions?.require_phone_verification && (
                <span className="py-0.5 px-4 flex justify-start items-center gap-2 rounded-lg text-xs font-medium bg-[#FFF3D6] text-[#A15C07]">
                  <IconAlertCircleFilled width={14} height={14} /> Es necesario
                  proporcionar un número de contacto válido
                </span>
              )}
            </div>
          </div>

          <div className="w-full mt-10 lg:mt-20">
            <TabsComponent customerInfo={customerData} />
          </div>
        </div>
      </div>
      {/* <section className='pt-24' >
                <div className="flex w-full items-center justify-center">
                    <div onClick={toggleAvatarModal}
                        className="w-32 h-32 rounded-full border-green501 border-8  flex justify-center">
                        <div className="rounded-full">
                            <img
                                src={apiData && apiData.avatar ? apiData.avatar : DEFAULT_AVATAR}
                                key={apiData && apiData.avatar ? apiData.avatar : DEFAULT_AVATAR}
                                className="rounded-full w-28 h-28 border-green500 border-6"
                                alt="avatar"
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null; // this prevents a checking looping
                                    currentTarget.src = DEFAULT_AVATAR
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="w-full text-center my-6 text-third text-lg">
                        Arrendatario
                    </div>
                    <div className="m-auto w-full">
                        <div className="w-full my-6 text-third text-4xl font-bold text-center">
                            {apiData?.name + ' ' + apiData?.last_name}
                        </div>
                    </div>
                </div>
            </section>
            <hr />
            <section>
                <div className="container">
                    <div className="m-auto w-full">
                        <div className="w-full my-10">
                            <div className="w-full flex justify-end lg:px-0 px-4">
                                <button className="text-green500" onClick={handleEdit}>
                                    Editar datos
                                </button>
                            </div>
                            <DetailSectionLabel
                                label="Nombres" value={apiData?.name} />
                            <DetailSectionLabel
                                label="Apellidos" value={apiData?.last_name} />
                            <DetailSectionLabel
                                label="Correo" value={apiData?.email} />
                            <DetailSectionLabel
                                label="Resumen de mi" value={apiData?.summary} />
                            <DetailSectionLabel
                                label="Estado o departamento" value={apiData?.state} />
                            <DetailSectionLabel
                                label="Teléfono" value={apiData?.phone} />
                            <DetailSectionLabel
                                label="Fecha de nacimiento" value={apiData?.birth_date} />
                            <DetailSectionLabel
                                label="Profesión" value={apiData?.profession} />
                            <DetailSectionLabel
                                label="Tamaño de mascota" value={mapPetSize(apiData?.pet_size)} />
                            <DetailSectionLabel
                                label="Sexo" value={mapGender(apiData?.gender)} />
                            <DetailSectionLabel
                                label="Cantidad de personas" value={apiData?.roommates_qty} />
                            <DetailSectionLabel
                                label="Pasatiempos" value={apiData?.hobbies} />
                            <DetailSectionLabel
                                label="Red social" value={apiData?.social_media} />
                            <DetailSectionLabel
                                label="Compañía donde labora" value={apiData?.company_name} />
                            <DetailSectionLabel
                                label="Salario neto" value={apiData?.net_income} />
                        </div>
                    </div>
                </div>
            </section> */}
    </>
  );
};

export default EditProfile;
